/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import differenceWith from 'lodash.differencewith';
import { action, extendObservable, observable } from 'mobx';
import { createEntityStore } from 'src/luis/core/stores/entityStore';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { ListEntityStore } from 'src/luis/types/stores/entities/ListEntityStore';
import { SublistStore } from 'src/luis/types/stores/entities/SublistStore';
import { SubtractStore } from 'src/luis/types/SubtractStore';

/**
 * A regex entity store factory function.
 *
 * @param id The id of the regex entity to create.
 * @param entity The regex entity data.
 * @returns A new instance of a regex entity store.
 */
export const createListEntityStore = (id: LuisId, entity: Partial<ListEntityStore>): ListEntityStore => {
    const entityStore = createEntityStore(id, { ...entity, typeId: ModelType.List });

    const listEntityStore: SubtractStore<ListEntityStore, EntityStore> = {
        sublists: entity.sublists ? entity.sublists : [],

        /**
         * Setter for the sublist stores.
         */
        setSublists(newSublists) {
            this.sublists = newSublists;
        },

        /**
         * Adds the given sublist to the list
         * of sublists.
         *
         * @param newSublist The sublist to add.
         */
        addSublist(newSublist: SublistStore) {
            this.sublists.unshift(newSublist);
        },

        /**
         * Sets the given sublist in the given index.
         *
         * @param index The index to replace
         * @param newSublist The sublist to replace with.
         */
        setSublist(index: number, newSublist: SublistStore) {
            this.sublists[index] = newSublist;
        },

        /**
         * Deletes the sublists with the given ids.
         *
         * @param ids The ids to delete.
         */
        deleteSublists(ids: SublistStore['id'][]) {
            this.sublists = differenceWith(this.sublists, ids, (sublist, sublistId) => sublist.id === sublistId);
        }
    };

    return extendObservable(
        entityStore,
        listEntityStore,
        { sublists: observable, setSublists: action, addSublist: action, setSublist: action, deleteSublists: action },
        { deep: false }
    );
};
